import { useCallback, useState } from 'react';

import { ExpandedState } from '~components/ExpandedStateButton/ExpandedStateButton';
import { useStores } from '~store';
import isEnumValue from '~utils/isEnumValue';

export type OrderCardsExpandedState = {
  allCards: `${ExpandedState}`;
  byOrderId: Record<string, `${ExpandedState}`>;
};

export const DEFAULT_ORDER_CARDS_EXPANDED_STATE: OrderCardsExpandedState = {
  allCards: ExpandedState.EXPANDED,
  byOrderId: {},
};

export default function useOrderCardsExpandedState(storageKey: string) {
  const { userStore } = useStores();

  const [orderCardsExpandedState, setOrderCardsExpandedState] =
    useState<OrderCardsExpandedState>(() => {
      const storedAllCards = localStorage.getItem(storageKey) ?? '';
      const collapseByDefault = userStore.companySettings.isEnabled(
        'collapse_dispatch_order_cards_by_default',
      );
      const companyDefault = collapseByDefault
        ? ExpandedState.COLLAPSED
        : ExpandedState.EXPANDED;

      return {
        allCards: isEnumValue(ExpandedState, storedAllCards)
          ? storedAllCards
          : companyDefault,
        byOrderId: {},
      };
    });

  const clearOrderCardsOverrides = useCallback(() => {
    setOrderCardsExpandedState((state) => ({ ...state, byOrderId: {} }));
  }, []);

  const toggleAllOrderCardsExpandedState = useCallback(
    (expandedState: `${ExpandedState}`) => {
      setOrderCardsExpandedState({ allCards: expandedState, byOrderId: {} });
      localStorage.setItem(storageKey, expandedState);
    },
    [storageKey],
  );

  const toggleSingleOrderCardsExpandedState = useCallback(
    (orderId: string, expandedState: `${ExpandedState}`) => {
      setOrderCardsExpandedState((state) => ({
        ...state,
        byOrderId: { ...state.byOrderId, [orderId]: expandedState },
      }));
    },
    [],
  );

  return {
    orderCardsExpandedState,
    clearOrderCardsOverrides,
    toggleAllOrderCardsExpandedState,
    toggleSingleOrderCardsExpandedState,
  };
}
